import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CloudUpload, Download } from "lucide-react";
import apiHelper from "../../apiHelper";
import Pagination from "../../components/Pagination";
import DiscardConfirmationModal from "../../components/DiscardConfirmationModal";
import downloadXlsx from "../../utils/downloadXlsx";
import MultiSelectChips from "../../components/MultiSelectChips";

// ERP-5254 — Platform Listing → Import: Excel upload of new listing mapping
// relationships. Same three-step shell as the Platform Pricing import
// (pages/platformpricing/PlatformPricingImport.js, Figma "ERP-5254 / S1").
//
//   01 Download and Upload  download the template (optionally filtered: Condition
//                           L1 / L2), then drop / browse the edited .xlsx (≤ 100MB)
//   02 Data Validation  the backend parses the file, then validates it in the
//                       background (platform lookups can take minutes); this page
//                       stays on the scanning screen and polls until the batch is
//                       'validated' (issues table / all valid) or 'failed'.
//                       Leaving the page while it runs means re-uploading.
//   03 Complete         — not built yet
//
// Backend: sales/listing_upload.py (GET sales/listing/export-template/,
// POST sales/listing/upload-batch/, GET .../<id>/, .../issues/, .../validation-result/).

const YELLOW = "#FFEB00";
const CIRCLE = 36;
const MAX_FILE_BYTES = 100 * 1024 * 1024;
const PAGE_SIZE = 10;
const POLL_MS = 3000;

const STEPS = [
  { num: "01", label: "Download and Upload" },
  { num: "02", label: "Data Validation" },
  { num: "03", label: "Complete" },
];

const FONT = "Montserrat";

// Condition L2 is linked to L1: only the L2 values that belong to the chosen L1s
// are offered; nothing chosen = all (same as the pricing template filters).
const CONDITION_L1 = ["New", "Used"];
const CONDITION_L2_BY_L1 = {
  New: ["Activated", "Not Activated", "NA", "Do Not Map"],
  Used: ["Pristine", "Mint", "Satisfactory", "Need to Repair", "Do Not Map"],
};
const l2OptionsFor = (l1s) => {
  const keys = l1s.length ? l1s : CONDITION_L1;
  const out = [];
  keys.forEach((k) => (CONDITION_L2_BY_L1[k] || []).forEach((v) => { if (!out.includes(v)) out.push(v); }));
  return out;
};

const spinStyle = { display: "inline-block", width: 16, height: 16, border: "2px solid #CED4DA", borderTopColor: "#6C757D", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 };

function Stepper({ currentStep }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", padding: "20px 100px", marginBottom: 12, borderRadius: 12, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.10)", boxSizing: "border-box" }}>
      {STEPS.map((step, idx) => {
        const n = idx + 1;
        const isActive = n === currentStep;
        const isDone = n < currentStep;
        const leftBlack = n <= currentStep;
        const rightBlack = n + 1 <= currentStep;
        const line = leftBlack && rightBlack ? "#212529"
          : leftBlack ? "linear-gradient(to right, #212529 50%, #CED4DA 50%)"
            : rightBlack ? "linear-gradient(to right, #CED4DA 50%, #212529 50%)" : "#CED4DA";
        return (
          <React.Fragment key={step.num}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: CIRCLE, flexShrink: 0 }}>
              <div style={{ width: CIRCLE, height: CIRCLE, borderRadius: "50%", background: isActive || isDone ? YELLOW : "transparent", border: isActive || isDone ? "none" : "1px solid #CED4DA", color: isActive || isDone ? "#000" : "#CED4DA", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 16, fontFamily: FONT, boxSizing: "border-box" }}>
                {step.num}
              </div>
              <span style={{ fontSize: 16, fontWeight: 600, fontFamily: FONT, color: isActive || isDone ? "#212529" : "#6C757D", whiteSpace: "nowrap" }}>{step.label}</span>
            </div>
            {idx < STEPS.length - 1 && <div style={{ flex: 1, height: 1, background: line, alignSelf: "flex-start", marginTop: CIRCLE / 2 }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function PlatformListingImport() {
  const history = useHistory();
  const fileInputRef = useRef(null);
  const allowNavigationRef = useRef(false);

  const uploadInFlightRef = useRef(false);
  const pollRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState(null);

  // step 1 · 01 Download Template
  const [fCondL1, setFCondL1] = useState([]);
  const [fCondL2, setFCondL2] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [fieldError, setFieldError] = useState("");

  // step 2 · Data Validation
  const [isScanning, setIsScanning] = useState(false);
  const [batch, setBatch] = useState(null);          // validated batch (detail dict)
  const [issues, setIssues] = useState([]);           // current page of invalid rows
  const [issueCount, setIssueCount] = useState(0);
  const [issuePage, setIssuePage] = useState(1);
  const [issueOrder, setIssueOrder] = useState("row_no");
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [downloadingResult, setDownloadingResult] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, title: "", message: "" });
  // step 3
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardSource, setDiscardSource] = useState(null);
  const [nextLocation, setNextLocation] = useState(null);

  const isDirty = !!file || !!batch;

  // Land on step 2's loading screen when ANY validation is still running: from
  // "View Task" on Platform Listing (batch id in the router state), or on a plain
  // open / refresh of this page (GET upload-batch/in-progress/). Whoever started
  // it, the wizard shows that one - so a second upload cannot be started while it
  // runs (Jess, 19 Oct). Nothing else is restored.
  const location = useLocation();
  useEffect(() => {
    const resume = (id) => { setCurrentStep(2); setIsScanning(true); pollBatch(id); };
    const id = location.state && location.state.resumeBatchId;
    if (id) {
      history.replace(location.pathname, {});
      resume(id);
      return;
    }
    (async () => {
      try {
        const res = await apiHelper("sales/listing/upload-batch/in-progress/", "GET");
        if (!res || !res.ok) return;
        const data = await res.json().catch(() => null);
        if (data && data.id && data.status === "validating") resume(data.id);
      } catch (e) { /* no running batch known - stay on step 1 */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── navigation guards ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isDirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [isDirty]);

  useEffect(() => {
    const unblock = history.block((tx) => {
      if (!isDirty) return true;
      if (allowNavigationRef.current) { allowNavigationRef.current = false; return true; }
      setDiscardSource("navigate");
      setNextLocation(tx);
      setDiscardOpen(true);
      return false;
    });
    return () => unblock();
  }, [isDirty, history]);

  // ── step 1: download template ────────────────────────────────────────────
  const l2Options = l2OptionsFor(fCondL1);
  const setCondL1 = (next) => {
    setFCondL1(next);
    const allowed = l2OptionsFor(next);
    setFCondL2((cur) => cur.filter((v) => allowed.includes(v)));   // drop L2s that no longer belong
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError("");
    try {
      const params = new URLSearchParams();
      if (fCondL1.length) params.set("condition_l1", fCondL1.join(","));
      if (fCondL2.length) params.set("condition_l2", fCondL2.join(","));
      const qs = params.toString();
      const ok = await downloadXlsx(`sales/listing/export-template/${qs ? `?${qs}` : ""}`, "Platform_Listing_Template.xlsx");
      if (!ok) setDownloadError("Download failed — please try again");
    } catch (e) {
      setDownloadError("Download failed — please try again");
    } finally {
      setDownloading(false);
    }
  };

  // ── step 1: file ─────────────────────────────────────────────────────────
  const processFile = (f) => {
    if (!f) return;
    setUploadError("");
    setFieldError("");
    if (!/\.xlsx$/i.test(f.name) || f.size > MAX_FILE_BYTES) {
      setUploadError("Upload failed — Only Excel (.xlsx) file below 100MB is supported");
      return;
    }
    setFile(f);
  };
  const handleBrowse = () => fileInputRef.current && fileInputRef.current.click();
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); processFile(e.dataTransfer.files[0]); };
  const removeFile = () => { setFile(null); setUploadError(""); if (fileInputRef.current) fileInputRef.current.value = null; };

  // ── step 2: upload, then poll the background validation ─────────────────
  const stopPolling = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);   // unmount = the user left; the backend keeps going on its own

  const loadIssues = async (batchId, page, ordering) => {
    setIssuesLoading(true);
    try {
      const res = await apiHelper(`sales/listing/upload-batch/${batchId}/issues/?page=${page}&page_size=${PAGE_SIZE}&ordering=${ordering}`, "GET");
      if (res && res.ok) {
        const data = await res.json().catch(() => ({}));
        setIssues(data.results || []);
        setIssueCount(data.count || 0);
      }
    } finally {
      setIssuesLoading(false);
    }
  };

  const pollBatch = async (batchId) => {
    try {
      const res = await apiHelper(`sales/listing/upload-batch/${batchId}/`, "GET");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      if (data.status === "validated") {
        setBatch(data);
        if (data.issue_count > 0) await loadIssues(data.id, 1, issueOrder);
        setIsScanning(false);
        return;
      }
      if (data.status === "failed") {
        setErrorModal({ show: true, title: "Upload failed", message: data.error_message || "The file could not be validated. Please try again." });
        setIsScanning(false);
        return;
      }
      pollRef.current = setTimeout(() => pollBatch(batchId), POLL_MS);   // still validating
    } catch (e) {
      pollRef.current = setTimeout(() => pollBatch(batchId), POLL_MS);   // transient: keep asking
    }
  };

  const runValidation = async () => {
    if (uploadInFlightRef.current) return;
    uploadInFlightRef.current = true;
    setIsScanning(true);
    setCurrentStep(2);
    setBatch(null);
    setIssues([]);
    setIssueCount(0);
    setIssuePage(1);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await apiHelper("sales/listing/upload-batch/", "POST", form, "multipart/form-data");
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorModal({
          show: true,
          title: data.no_valid_rows ? "No valid rows found" : "Upload failed",
          message: data.detail || data.message || "The uploaded file could not be processed. Please check and upload again.",
        });
        setIsScanning(false);
        return;
      }
      pollBatch(data.id);
    } catch (e) {
      setErrorModal({ show: true, title: "Upload failed", message: String(e) });
      setIsScanning(false);
    } finally {
      uploadInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (batch && batch.issue_count > 0 && currentStep === 2 && !isScanning) loadIssues(batch.id, issuePage, issueOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuePage, issueOrder]);

  const closeErrorModal = () => { setErrorModal({ show: false, title: "", message: "" }); setBatch(null); setCurrentStep(1); };

  const downloadValidation = async () => {
    if (!batch || downloadingResult) return;
    setDownloadingResult(true);
    try { await downloadXlsx(`sales/listing/upload-batch/${batch.id}/validation-result/`, "validation.xlsx"); }
    finally { setDownloadingResult(false); }
  };

  // ── header buttons ───────────────────────────────────────────────────────
  const handleNext = () => {
    if (currentStep === 1) {
      if (!file) { setFieldError("Upload File is required"); return; }
      runValidation();
    } else if (currentStep === 2) {
      if (isScanning || !batch) return;
      setSaveError("");
      setCurrentStep(3);
    }
  };
  const handleBack = () => {
    if (currentStep === 2) { stopPolling(); setIsScanning(false); setBatch(null); setCurrentStep(1); }
    else if (currentStep === 3) { setCurrentStep(2); }
  };

  // ── step 3: save ─────────────────────────────────────────────────────────
  // Save writes the mappings (create / update / delete) in the ERP and starts the
  // stock-qty push in the backend; the Platform Listing page's banner follows it.
  const handleSave = async () => {
    if (!batch || isSaving) return;
    setIsSaving(true);
    setSaveError("");
    try {
      const res = await apiHelper(`sales/listing/upload-batch/${batch.id}/complete/`, "POST", {});
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.detail || "Save failed — please try again");
        return;
      }
      allowNavigationRef.current = true;
      history.push("/listing-pricing", { success: "Listing Mappings Saved Successfully" });
    } catch (e) {
      setSaveError(String(e));
    } finally {
      setIsSaving(false);
    }
  };
  const handleDiscard = () => { setDiscardSource("discard"); setDiscardOpen(true); };
  const confirmDiscard = () => {
    allowNavigationRef.current = true;
    setDiscardOpen(false);
    if (discardSource === "navigate" && nextLocation) history.push(nextLocation.pathname, nextLocation.state || {});
    else history.push("/listing-pricing");
  };

  const btnBase = { display: "flex", height: 34, minWidth: 100, padding: "0 10px", justifyContent: "center", alignItems: "center", gap: 10, borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer", boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)" };
  const disabledStyle = { border: "1px solid #ADB5BD", background: "#E9ECEF", color: "#ADB5BD", cursor: "not-allowed" };
  const lightStyle = { border: "1px solid #212529", background: "#fff", color: "#212529" };
  const darkStyle = { border: "1px solid #212529", background: "#212529", color: "#fff" };
  const isBusy = isScanning || isSaving;
  const nextDisabled = isBusy || (currentStep === 2 && !batch);
  const saveDisabled = isBusy || !batch;
  const totalIssuePages = Math.max(1, Math.ceil(issueCount / PAGE_SIZE));
  const stepTitle = currentStep === 1 ? "Download and Upload" : currentStep === 2 ? "Data Validation" : "Complete";
  const summary = (batch && batch.summary) || { platforms: [], total: null };

  return (
    <div className="company-body main-company-body-inner new-look">
      <style>{"@keyframes spin { to { transform: rotate(360deg); } } .erp-dl-btn:hover:not(:disabled) { background: #fff !important; color: #212529 !important; } .erp-scanner-ring { width: 88px; height: 88px; border-radius: 50%; border: 8px solid #E9ECEF; border-top-color: #FFEB00; animation: spin 0.9s linear infinite; margin-bottom: 24px; }"}</style>

      {/* Breadcrumb */}
      <div className="breadcrumb" style={{ marginBottom: 8 }}>
        <span className="breadcrumb-item">Listing</span>
        <span className="breadcrumb-item clickable" style={{ cursor: "pointer" }} onClick={() => history.push("/listing-pricing")}>Platform Listing</span>
        <span className="breadcrumb-item">Import</span>
      </div>

      <Stepper currentStep={currentStep} />

      {/* Header card */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 20, background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontFamily: FONT, fontWeight: 700, fontSize: 20, color: "#212529" }}>{stepTitle}</h4>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" onClick={handleDiscard} disabled={isBusy} style={{ ...btnBase, ...(isBusy ? disabledStyle : lightStyle) }}>Discard</button>
          {currentStep > 1 && (
            <button type="button" onClick={handleBack} disabled={isBusy} style={{ ...btnBase, ...(isBusy ? disabledStyle : lightStyle) }}>Back</button>
          )}
          {currentStep < 3 ? (
            <button type="button" onClick={handleNext} disabled={nextDisabled} style={{ ...btnBase, ...(nextDisabled ? disabledStyle : darkStyle) }}>Next</button>
          ) : (
            <button type="button" onClick={handleSave} disabled={saveDisabled} style={{ ...btnBase, ...(saveDisabled ? disabledStyle : darkStyle) }}>
              {isSaving ? "Saving…" : "Save"}
              {isSaving && <span style={spinStyle} />}
            </button>
          )}
        </div>
      </div>

      {/* ── STEP 1 ── */}
      {currentStep === 1 && (
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", padding: 24 }}>
        {/* 01 Download Template */}
        <div style={{ borderLeft: "2px solid #FFEB00", paddingLeft: 16, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: "#ADB5BD" }}>01</span>
            <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: "#212529" }}>Download Template</span>
          </div>
          <p style={{ fontFamily: FONT, fontSize: 14, color: "#212529", marginBottom: 12 }}>
            Download the latest Template with all / filtered Active Stocks and their mapped Listings.
          </p>
          <div style={{ border: "1px solid #CED4DA", borderRadius: 10, padding: 16, background: "#F8F9FA" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 12, rowGap: 12 }}>
              {[
                ["Condition L1", fCondL1, setCondL1, CONDITION_L1],
                ["Condition L2", fCondL2, setFCondL2, l2Options],
              ].map(([label, val, set, opts]) => (
                <div key={label} style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: "#212529", marginBottom: 6 }}>{label}</div>
                  <MultiSelectChips fixedHeight selected={val} onChange={set} options={opts} placeholder="Select..." />
                </div>
              ))}
            </div>
            <p style={{ fontFamily: FONT, fontSize: 12, color: "#6C757D", margin: "12px 0 12px" }}>
              If you don't set any filters, all Active Stocks and their mapped Listings will be exported by default.
            </p>
            <button type="button" className="erp-dl-btn" onClick={handleDownload} disabled={downloading}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: 10, borderRadius: 8, border: `1px solid ${downloading ? "#CED4DA" : "#212529"}`, boxShadow: downloading ? "0 1px 4px 0 rgba(0,0,0,0.10)" : "none", background: downloading ? "#E9ECEF" : "#212529", color: downloading ? "#6C757D" : "#fff", fontFamily: FONT, fontSize: 12, fontWeight: 600, lineHeight: "16px", cursor: downloading ? "progress" : "pointer" }}>
              Download
              {downloading ? <span style={spinStyle} /> : <Download size={16} />}
            </button>
            {downloadError && <p style={{ color: "#DC3545", fontFamily: FONT, fontSize: 12, margin: "8px 0 0" }}>{downloadError}</p>}
          </div>
        </div>

        {/* 02 Upload File */}
        <div style={{ borderLeft: "2px solid #FFEB00", paddingLeft: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: "#ADB5BD" }}>02</span>
            <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: "#212529" }}>Upload File <span style={{ color: "#DC3545" }}>*</span></span>
          </div>
          <p style={{ fontFamily: FONT, fontSize: 14, color: "#212529", marginBottom: 12 }}>Upload the Excel with new Listing mapping relationships.</p>
          <div
            onClick={!file ? handleBrowse : undefined}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={handleDrop}
            style={{ border: `1.5px dashed ${isDragging ? "#212529" : (uploadError || fieldError) ? "#DC3545" : "#CED4DA"}`, borderRadius: 12, padding: 16, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 120, cursor: file ? "default" : "pointer", background: isDragging ? "#F8F9FA" : "#fff", position: "relative", boxSizing: "border-box" }}
          >
            <input type="file" accept=".xlsx" ref={fileInputRef} style={{ display: "none" }} onChange={(e) => processFile(e.target.files[0])} />
            {file ? (
              <div style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ position: "absolute", top: -4, right: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <button type="button" title="Replace file" onClick={(e) => { e.stopPropagation(); removeFile(); fileInputRef.current && fileInputRef.current.click(); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#6C757D", padding: 0, display: "flex" }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5Z" stroke="#6C757D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                  <button type="button" title="Remove file" onClick={(e) => { e.stopPropagation(); removeFile(); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#6C757D", fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
                </div>
                <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
                  <path d="M4 0 H28 L40 12 V44 Q40 48 36 48 H4 Q0 48 0 44 V4 Q0 0 4 0 Z" fill="white" stroke="#E0E0E0" strokeWidth="1.5" />
                  <path d="M28 0 L40 12 H32 Q28 12 28 8 Z" fill="#E0E0E0" />
                  <rect x="0" y="22" width="28" height="20" rx="3" fill="#107C41" />
                  <text x="4" y="37" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" fill="white">X</text>
                </svg>
                <span style={{ fontFamily: FONT, fontSize: 13, color: "#212529" }}>{file.name} ({(file.size / (1024 * 1024)).toFixed(1)}MB)</span>
              </div>
            ) : (
              <>
                <CloudUpload size={24} color="black" style={{ marginBottom: 8 }} />
                <p style={{ fontFamily: FONT, fontSize: 12, color: "#6C757D", margin: 0 }}>
                  Drag your Excel (.xlsx) here or <span style={{ color: "#0D6EFD", fontWeight: 600, textDecoration: "underline" }}>Browse</span>
                </p>
              </>
            )}
          </div>
          {uploadError && <p style={{ color: "#DC3545", fontFamily: FONT, fontSize: 12, marginTop: 8 }}>{uploadError}</p>}
          {fieldError && !uploadError && <p style={{ color: "#DC3545", fontFamily: FONT, fontSize: 12, marginTop: 8 }}>{fieldError}</p>}
        </div>
      </div>
      )}

      {/* ── STEP 2: scanning (the backend validates in the background; we poll) ── */}
      {currentStep === 2 && isScanning && (
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", padding: 24 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 20, border: "1px solid #FFEB00", borderRadius: 12, background: "#FFFEF0", padding: 16, marginBottom: 20 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01M21.73 18L13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" stroke="#FFC107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <div>
              <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 16, color: "#212529", marginBottom: 10 }}>Do not close or leave this page</div>
              <div style={{ fontFamily: FONT, fontSize: 14, color: "#212529" }}>Your file is being scanned. Navigating away or closing this page will interrupt the process and you will need to re-upload your file.</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0 40px" }}>
            <div className="erp-scanner-ring" />
            <h5 style={{ fontFamily: FONT, fontWeight: 700, fontSize: 20, color: "#212529", margin: "0 0 10px" }}>Scanning your file for issues...</h5>
            <p style={{ fontFamily: FONT, fontSize: 14, color: "#6C757D", margin: 0 }}>This usually take a few seconds.</p>
          </div>
        </div>
      )}

      {/* ── STEP 2: all valid ── */}
      {currentStep === 2 && !isScanning && batch && batch.issue_count === 0 && !errorModal.show && (
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", padding: "40px 20px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <svg width="101" height="106" viewBox="0 0 101 106" fill="none" style={{ marginBottom: 24 }}>
            <circle cx="50.14" cy="55.36" r="50.14" fill="#F8F9FA" />
            <path d="M78.77 96.06H23.76c-1.7 0-3.08-1.38-3.08-3.08V17.74c0-1.7 1.38-3.08 3.08-3.08h38.02l20.07 19.96v58.36c0 1.7-1.38 3.08-3.08 3.08Z" fill="#FFEB00" stroke="#000" strokeWidth="2" />
            <path d="M61.78 14.66v16.88c0 1.7 1.38 3.08 3.08 3.08h17" stroke="#000" strokeWidth="2" />
            <ellipse cx="42.4" cy="58.9" rx="2.94" ry="2.95" fill="#000" /><ellipse cx="60.05" cy="58.9" rx="2.94" ry="2.95" fill="#000" />
            <path d="M45.35 68.93c3.62 2.18 8.14 2.18 11.76 0" stroke="#000" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="78.11" cy="88.02" r="15.5" fill="#000" />
            <path d="M87.08 82.4 75.02 94.46l-5.48-5.48" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h5 style={{ fontFamily: FONT, fontWeight: 600, fontSize: 20, color: "#212529", margin: "0 0 8px" }}>All Rows are Valid</h5>
          <p style={{ fontFamily: FONT, fontSize: 14, color: "#6C757D", margin: "0 0 28px" }}>You can proceed to the next step.</p>
          <button type="button" onClick={downloadValidation} disabled={downloadingResult} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: downloadingResult ? "not-allowed" : "pointer", background: "#212529", color: "#fff", border: "none" }}>
            Validation Result {downloadingResult ? <span style={spinStyle} /> : <Download size={16} />}
          </button>
        </div>
      )}

      {/* ── STEP 2: issues ── */}
      {currentStep === 2 && !isScanning && batch && batch.issue_count > 0 && !errorModal.show && (
        <>
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: 20, marginBottom: 12 }}>
            <button type="button" onClick={downloadValidation} disabled={downloadingResult} style={{ display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 16px", borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: downloadingResult ? "not-allowed" : "pointer", background: "#212529", color: "#fff", border: "none" }}>
              Validation Result {downloadingResult ? <span style={spinStyle} /> : <Download size={16} />}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #E9ECEF", borderRadius: 8, padding: "8px 14px", fontFamily: FONT, fontSize: 12, color: "#212529" }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="0.5" y="0.5" width="19" height="19" rx="9.5" fill="#DC3545" stroke="#000" /><path d="M13.75 6.25 6.25 13.75M6.25 6.25l7.5 7.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
              <span>Fatal: <strong>{batch.issue_count}</strong></span>
              <span>( Please fix them and re-upload, or skip these rows and continue.</span>
            </div>
          </div>
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", padding: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
              <thead>
                <tr style={{ background: "#E9ECEF" }}>
                  <th onClick={() => { setIssueOrder((o) => (o === "row_no" ? "-row_no" : "row_no")); setIssuePage(1); }} style={{ height: 45, padding: 10, width: 100, cursor: "pointer", userSelect: "none", textAlign: "left" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, color: "#212529" }}>
                      Row #
                      <span style={{ display: "flex", flexDirection: "column" }}>
                        <svg width={12} height={12} viewBox="0 0 24 24" fill={issueOrder === "row_no" ? "#212529" : "#6C757D"}><path d="M12 8l6 6H6l6-6z" /></svg>
                        <svg style={{ marginTop: -7 }} width={12} height={12} viewBox="0 0 24 24" fill={issueOrder === "-row_no" ? "#212529" : "#6C757D"}><path d="M12 16l-6-6h12l-6 6z" /></svg>
                      </span>
                    </span>
                  </th>
                  <th style={{ height: 45, padding: 10, fontSize: 12, fontWeight: 700, color: "#212529", textAlign: "left" }}>Issue Details</th>
                  <th style={{ height: 45, padding: 10, fontSize: 12, fontWeight: 700, color: "#212529", textAlign: "left", width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((r, idx) => (
                  <tr key={r.id} style={{ borderTop: "1px solid #E9ECEF", background: idx % 2 === 1 ? "#F8F9FB" : "#fff" }}>
                    <td style={{ padding: 10, fontSize: 12, color: "#212529" }}>{r.row}</td>
                    <td style={{ padding: 10, fontSize: 12, color: "#212529" }}>{r.issue}</td>
                    <td style={{ padding: 10 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 12, color: "#212529", margin: 0, cursor: "not-allowed" }} title="Rows with issues are skipped — fix them in the file and re-upload to include them">
                        <span style={{ width: 18, height: 18, borderRadius: "50%", background: YELLOW, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#212529" }} />
                        </span>
                        Skip
                      </label>
                    </td>
                  </tr>
                ))}
                {issues.length === 0 && issuesLoading && (
                  <tr><td colSpan={3} style={{ padding: 20, textAlign: "center", fontSize: 12, color: "#6C757D" }}>Loading…</td></tr>
                )}
              </tbody>
            </table>
            <div style={{ borderTop: "1px solid #E9ECEF", padding: "12px 16px", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
              <span style={{ fontFamily: FONT, fontSize: 13, color: "#6C757D", whiteSpace: "nowrap" }}>
                {issueCount === 0 ? "0 - 0 of 0" : `${(issuePage - 1) * PAGE_SIZE + 1} - ${Math.min(issuePage * PAGE_SIZE, issueCount)} of ${issueCount}`}
              </span>
              <Pagination currentPage={issuePage} totalPages={totalIssuePages} onPageChange={setIssuePage} />
            </div>
          </div>
        </>
      )}

      {/* ── STEP 3 (Figma S3 "Complete"): per-platform summary, Save ── */}
      {currentStep === 3 && batch && (
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", overflow: "hidden", marginBottom: 12 }}>
          <div style={{ padding: "15px 20px", background: "#F8F9FA" }}>
            <span style={{ fontFamily: FONT, fontWeight: 600, fontSize: 14, color: "#212529" }}>Summary</span>
          </div>
          <div style={{ padding: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT, borderBottom: "1px solid #DEE2E6" }}>
              <thead>
                <tr style={{ background: "#E9ECEF" }}>
                  {["Platform", "New Mapping", "Updated Mapping"].map((c) => (
                    <th key={c} style={{ padding: 10, textAlign: "left", fontSize: 12, fontWeight: 700, color: "#212529", borderTop: "1px solid #E9ECEF", borderBottom: "1px solid #E9ECEF" }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.platforms.map((p, idx) => (
                  <tr key={p.platform} style={{ background: idx % 2 === 1 ? "#F8F9FB" : "#fff" }}>
                    <td style={{ padding: 10, fontSize: 12, color: "#1F1F1F" }}>{p.platform}</td>
                    <td style={{ padding: 10, fontSize: 12, color: "#1F1F1F" }}>{p.new}</td>
                    <td style={{ padding: 10, fontSize: 12, color: "#1F1F1F" }}>{p.updated}</td>
                  </tr>
                ))}
                {summary.total && (
                  <tr style={{ background: summary.platforms.length % 2 === 1 ? "#F8F9FB" : "#fff", fontWeight: 600 }}>
                    <td style={{ padding: 10, fontSize: 12, color: "#1F1F1F" }}>Total</td>
                    <td style={{ padding: 10, fontSize: 12, color: "#1F1F1F" }}>{summary.total.new}</td>
                    <td style={{ padding: 10, fontSize: 12, color: "#1F1F1F" }}>{summary.total.updated}</td>
                  </tr>
                )}
              </tbody>
            </table>
            {saveError && <p style={{ color: "#DC3545", fontFamily: FONT, fontSize: 12, marginTop: 12 }}>{saveError}</p>}
          </div>
        </div>
      )}

      <DiscardConfirmationModal
        isOpen={discardOpen}
        entityName="Listing Import"
        onCancel={() => { setDiscardOpen(false); setNextLocation(null); setDiscardSource(null); }}
        onConfirm={confirmDiscard}
      />

      {/* "No valid rows found" / upload error modal */}
      {errorModal.show && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 32, width: 560, position: "relative", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", fontFamily: FONT, boxSizing: "border-box" }}>
            <button type="button" onClick={closeErrorModal} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "#212529", fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
            <h4 style={{ margin: "0 0 12px", fontWeight: 600, fontSize: 20, color: "#212529" }}>{errorModal.title}</h4>
            <p style={{ margin: "0 0 24px", fontSize: 14, color: "#6C757D", lineHeight: 1.6 }}>{errorModal.message}</p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={closeErrorModal} style={{ background: "#212529", color: "#fff", border: "none", width: 100, height: 34, borderRadius: 8, fontFamily: FONT, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
